/**
 * 🔁 طبقة الاتصال لتبويب «المهام المتكررة» جوّه شاشة «فريقي».
 *
 * العقد مع السيستم:
 *   • `GET    /task-recurrences`                  — القوالب (نطاق أقسامك)
 *   • `POST   /task-recurrences`                  — قالب جديد
 *   • `PATCH  /task-recurrences/{id}`             — تعديل (بـ`If-Match`)
 *   • `POST   /task-recurrences/{id}/activate`    — تشغيل
 *   • `POST   /task-recurrences/{id}/deactivate`  — إيقاف
 *   • `POST   /task-recurrences/{id}/delete`      — حذف ناعم بسبب
 *
 * ⛔ الصلاحية للمنسق والمدير بس (`task.create_for_others`) — والشاشة
 *    بتخفي التبويب من غيرها بدل ما تودّي الموظف على 403.
 * ⛔ ممنوع «نجاح» على رد فاضي: أي رد مش قائمة/مش كائن بيرمي خطأ صريح.
 * ⛔ النصوص المعروضة (وصف الجدول · التحذير) **بتيجي من السيستم** —
 *    الواجهة ما بتركّبش جمل من عندها.
 */
import { api, ApiError } from "@/api/client";
import type { Envelope, ListMeta } from "@/api/types";

/* ══════════════ أنواع ══════════════ */

export type RecurrenceFrequency = "daily" | "weekly";

export type RecurrenceChecklistItem = {
  key: string;
  label_ar: string;
  required: boolean;
};

export type Recurrence = {
  id: string;
  title_ar: string;
  body_ar: string;
  dept: string | null;
  assignee_id: string | null;
  priority: "normal" | "urgent";
  sla_hours: number | null;
  checklist: RecurrenceChecklistItem[];
  frequency: RecurrenceFrequency;
  /** أرقام 0..6 — **الاتنين = 0** (نفس `datetime.weekday()` في السيستم) */
  days_of_week: number[];
  /** `HH:MM` بتوقيت الأكاديمية */
  run_at_local: string;
  timezone: string | null;
  catchup_days: number;
  is_active: boolean;
  /** «كل يوم 9:00ص» — جاهز من السيستم */
  schedule_label_ar: string;
  last_generated_date: string | null;
  last_generated_at: string | null;
  last_task_id: string | null;
  generated_count: number;
  /** خلل لازم يتقال للمنسقة (مسنَد له حساب مش شغّال) — `null` يعني تمام */
  warning_ar: string | null;
  version: number;
  updated_at: string | null;
};

export type RecurrenceInput = {
  title_ar: string;
  body_ar?: string;
  dept?: string;
  /** `""` أو `null` = سيبها لطابور القسم */
  assignee_id?: string | null;
  priority?: "normal" | "urgent";
  sla_hours?: number | null;
  checklist?: RecurrenceChecklistItem[];
  frequency?: RecurrenceFrequency;
  days_of_week?: number[];
  run_at_local?: string;
  catchup_days?: number;
};

export type RecurrencesPage = { rows: Recurrence[]; meta: ListMeta | null };

/**
 * ⛔ **الترتيب مقفول: الاتنين = 0** — زي `datetime.weekday()` في السيستم
 *    و`models/task_recurrence.WEEKDAY_LABELS_AR` بالحرف.
 *    القايمة متكررة هنا لأن الاختيار في الفورم محتاج الأسماء قبل أي طلب.
 *    🔒 فيه تست في الباك إند (`test_weekday_labels_match_the_frontend`)
 *    بيقرا الملف ده ويقارن الترتيب — لأن الانحراف هنا معناه إن المنسقة
 *    تختار «الأحد» والمهمة تنزل يوم الاتنين، وده غلط ما بيبانش غير بعد أسبوع.
 */
export const WEEKDAYS_AR: readonly string[] = [
  "الاتنين",
  "التلات",
  "الأربع",
  "الخميس",
  "الجمعة",
  "السبت",
  "الأحد",
];

/* ══════════════ حرّاس الرد الفاضي ══════════════ */

function shapeError(path: string, payload: unknown): ApiError {
  console.error(`[recurrences] ${path} رجّع شكل مش متوقّع`, payload);
  return new ApiError(
    500,
    { code: "BAD_SHAPE", message_ar: "السيستم رد ببيانات مش مفهومة — جرّب تاني" },
    null,
  );
}

function expectOne(res: Envelope<Recurrence>, path: string): Recurrence {
  const row = res.data as Recurrence | undefined | null;
  if (!row || typeof row !== "object" || typeof row.id !== "string") {
    throw shapeError(path, res.data);
  }
  return row;
}

/* ══════════════ النداءات ══════════════ */

export async function listRecurrences(opts: {
  dept?: string | null;
  isActive?: boolean | null;
  page?: number;
  perPage?: number;
  signal?: AbortSignal;
} = {}): Promise<RecurrencesPage> {
  const path = "/task-recurrences";
  const res = await api.get<Recurrence[]>(path, {
    query: {
      dept: opts.dept || undefined,
      is_active: opts.isActive === null || opts.isActive === undefined
        ? undefined
        : String(opts.isActive),
      page: opts.page ?? 1,
      per_page: opts.perPage ?? 50,
    },
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  if (!Array.isArray(res.data)) throw shapeError(path, res.data);
  return { rows: res.data, meta: res.meta ?? null };
}

export async function createRecurrence(input: RecurrenceInput): Promise<Recurrence> {
  const path = "/task-recurrences";
  return expectOne(await api.post<Recurrence>(path, input), path);
}

export async function updateRecurrence(
  id: string,
  changes: Partial<RecurrenceInput>,
  version: number,
): Promise<Recurrence> {
  const path = `/task-recurrences/${id}`;
  // 🔑 القفل التفاؤلي إجباري على كل كتابة — حد تاني عدّل ⇒ 409 بدل ما ندهس عليه
  return expectOne(await api.patch<Recurrence>(path, changes, { ifMatch: version }), path);
}

export async function setRecurrenceActive(
  id: string,
  active: boolean,
  version: number,
): Promise<Recurrence> {
  const path = `/task-recurrences/${id}/${active ? "activate" : "deactivate"}`;
  return expectOne(await api.post<Recurrence>(path, undefined, { ifMatch: version }), path);
}

export async function deleteRecurrence(
  id: string,
  reasonAr: string,
  version: number,
): Promise<Recurrence> {
  const path = `/task-recurrences/${id}/delete`;
  return expectOne(
    await api.post<Recurrence>(path, { reason_ar: reasonAr }, { ifMatch: version }),
    path,
  );
}

/* ══════════════ نصوص العرض ══════════════ */

/** «آخر نسخة: 2026-09-01» — أو الصمت لو لسه ما اشتغلتش ولا مرة */
export function lastRunLabelAr(row: Recurrence): string {
  if (!row.last_generated_date) {
    return row.is_active ? "لسه ما اشتغلتش ولا مرة" : "موقوفة";
  }
  return `آخر نسخة ${row.last_generated_date}`;
}
